"use client";

import type { Web3TxMultiCalcSWR } from "@/hooks/tx-flow/useDepositCalculate";
import type { SupTransactionType, SupWeb3Balance, SupWeb3DepositStep, SupWeb3TxSingleVault } from "@/types";
import React, { type Dispatch, Fragment, type SetStateAction, useMemo } from "react";

import { SupText } from "@repo/core-ui";
import { useAmountState } from "@/components/Web3/Common/AmountState";

import Web3CommonProgress from "@/components/Web3/Common/Progress";
import Web3WithdrawChainAlert from "@/components/Web3/Withdraw/ChainAlert";
import Web3CommonCartEmpty from "@/components/Web3/Common/Cart/Empty";
import Web3CommonCartClear from "@/components/Web3/Common/Cart/Clear";
import Web3CommonVaultSingle from "@/components/Web3/Common/Vault/Single";

interface Web3CommonVaultAccordionProps {
  vaultList: SupWeb3TxSingleVault[];
  step: SupWeb3DepositStep;
  setVaultList: Dispatch<SetStateAction<SupWeb3TxSingleVault[]>>;
  setStep: Dispatch<SetStateAction<SupWeb3DepositStep>>;
  selectedBalance?: SupWeb3Balance;
  type?: SupTransactionType;
  setPauseRequests: Dispatch<SetStateAction<boolean>>;
  pauseRequests: boolean;
  calculatedRoutes: Web3TxMultiCalcSWR;
}

export default function Web3CommonVaultAccordion(props: Web3CommonVaultAccordionProps) {
  const type = props.type ?? "deposit";
  const sortedVaultList = useMemo(() => props.vaultList.sort((a, b) => (a.id > b.id ? 1 : -1)), [props.vaultList]);

  const { totalAmount } = useAmountState();

  const showProgress = useMemo(() => {
    if (type === "deposit") {
      return props.selectedBalance && !!totalAmount && totalAmount !== "0";
    } else {
      const isAmountsReady = props.vaultList.filter(singleVault => !!singleVault.amount).length === props.vaultList.length;
      return props.selectedBalance && isAmountsReady;
    }
  }, [type, props.selectedBalance, props.vaultList, totalAmount]);

  const key = useMemo(() => {
    return JSON.stringify(sortedVaultList);
  }, [sortedVaultList]);

  return sortedVaultList.length > 0 ? (
    <Fragment>
      <header className="mb-0.5 flex h-10 w-full items-center justify-between">
        <SupText variant="subtitle1" family="mono" weight="bold">
          Bag
        </SupText>
        <div className="flex w-fit items-center justify-end">
          {showProgress && props.calculatedRoutes.timestamp && !props.calculatedRoutes.isLoading && (
            <Web3CommonProgress
              key={key}
              mutate={props.calculatedRoutes.mutate}
              calculateTimestamp={props.calculatedRoutes.timestamp}
              pauseRequests={props.pauseRequests}
              setPauseRequests={props.setPauseRequests}
            />
          )}
          <Web3CommonCartClear type={type} />
        </div>
      </header>
      <div className="scrollbar-custom flex h-full w-full flex-col space-y-4 overflow-y-auto overflow-x-hidden pb-1 pt-0">
        {props.type === "withdraw" && <Web3WithdrawChainAlert />}

        {sortedVaultList.map(vault => (
          <Web3CommonVaultSingle {...props} key={`${vault.id}`} singleVault={vault} />
        ))}
      </div>
    </Fragment>
  ) : (
    <Web3CommonCartEmpty type={props.type} />
  );
}
